import {
  isNullish,
  removeCommas,
  roundDownToNearestHundredth,
  parseUnsupportedDocumentError,
  parseMultiplePoliciesError,
} from '@shared';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { renderHeaderCell, renderTableCell, selectPolicyExtractor } from '..';
import { useSelector } from 'react-redux';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { DataTable } from './DataTable';
import { CircleCheckIcon, Info, TriangleAlert } from 'lucide-react';

type PolicyField = {
  fieldId: string;
  fieldTitle: string;
  fieldValue: string | number;
  pageNumber?: number;
  locationDescription?: string;
};

const columnHelper = createColumnHelper<PolicyField>();

const renderTableCellWrapper = (value: string | React.ReactNode) => {
  return renderTableCell(value, 'font-normal');
};

const columns = [
  columnHelper.accessor('fieldTitle', {
    header: () => renderHeaderCell('Field'),
    cell: info => renderTableCell(info.getValue()),
  }),
  columnHelper.accessor('fieldValue', {
    header: () => renderHeaderCell('Value'),
    cell: info => {
      const value = info.getValue();
      if (typeof value === 'number') {
        return renderTableCellWrapper(roundDownToNearestHundredth(value));
      } else if (typeof value === 'string') {
        const parsedValue = parseFloat(removeCommas(value));
        if (!isNaN(parsedValue)) {
          return renderTableCellWrapper(roundDownToNearestHundredth(parsedValue));
        }
      }
      return value;
    },
  }),
  columnHelper.accessor('locationDescription', {
    header: () => renderHeaderCell('Location'),
    cell: info => {
      const row = info.row.original;
      const location = info.getValue();
      const page = row.pageNumber;

      if (!location && !page) {
        return renderTableCellWrapper('');
      }

      if (page && location) {
        return renderTableCellWrapper(`Page ${page}; ${location}`);
      }

      if (page) {
        return renderTableCellWrapper(`Page ${page}`);
      }

      return renderTableCellWrapper(location ?? '');
    },
  }),
];

export function PolicyAnalysisTable() {
  const { processedResponse } = useSelector(selectPolicyExtractor);

  if (!processedResponse) {
    return null;
  }

  // Some policy types map two storage fields (e.g. a value and its `*AtIssue`
  // companion) to the same display title with the same extracted value. Collapse
  // those into a single row so the review table doesn't show redundant duplicates.
  // Rows with the same title but a genuinely different value still render separately.
  const seenTitleValueKeys = new Set<string>();
  const tableData = Object.entries(processedResponse)
    .filter(([_, field]) => !isNullish(field?.fieldValue))
    .map(([fieldId, field]) => ({
      fieldId,
      fieldTitle: field!.fieldTitle,
      fieldValue: field!.fieldValue,
      pageNumber: field!.pageNumber,
      locationDescription: field!.locationDescription,
    }))
    .filter(row => {
      const key = `${row.fieldTitle}|${row.fieldValue}`;
      if (seenTitleValueKeys.has(key)) {
        return false;
      }
      seenTitleValueKeys.add(key);
      return true;
    });

  return (
    <div className="flex w-full flex-col gap-2">
      {/* Table Header and Description */}
      <div className="flex flex-col gap-2">
        {/* Header */}
        <div className="flex items-center gap-2">
          <h1 className="text-primary-dark text-base font-semibold leading-6">
            Policy Upload Results
          </h1>
          <CircleCheckIcon className="h-4 w-4 text-green-500" />
        </div>
        {/* Description */}
        <p className="text-sm text-gray-700">
          The below table contains the fields that were extracted from the policy document. Any
          fields that were not found are not displayed.
        </p>
      </div>
      <DataTable
        columns={columns as ColumnDef<PolicyField>[]}
        data={tableData}
        tableId="policy-analysis"
        getRowId={row => `${row.fieldId}`}
        variant="outlined"
        hideColumnSelector={true}
        pageSize={50}
        className="my-4"
      />
    </div>
  );
}

export function ImageBasedPdfDisclaimer({ className = '' }: { className?: string }) {
  return (
    <div className={`my-2 rounded-md border-0 bg-gray-50 p-3 ${className}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <Info
            aria-hidden="true"
            className="h-5 w-5 text-gray-700"
          />
        </div>
        <div className="ml-3">
          <p className="text-sm text-gray-800">
            We couldn't read this PDF. We only support text-based PDFs. If you have an image-based
            PDF, please request a text version from your carrier or contact us at{' '}
            <a
              href="mailto:hello@joinwaterlily.com"
              className="text-primary hover:underline"
            >
              hello@joinwaterlily.com
            </a>{' '}
            and we'll convert it for you.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Returns the first error the parser recognizes. Errors accumulate, and a generic one (e.g. a
 * failed poll) can land before the coded error the worker stores, so we scan the whole list
 * instead of only looking at the first entry.
 */
function findCodedError<T>(errors: string[], parse: (error: string) => T | null): T | null {
  for (const error of errors) {
    const parsed = parse(error);
    if (parsed) {
      return parsed;
    }
  }
  return null;
}

export function PolicyAnalysisErrorAlert() {
  const { status, errors } = useSelector(selectPolicyExtractor);

  if (status !== 'error' || errors.length === 0) {
    return null;
  }

  const maybeImageBasedPdfError = errors.some(error =>
    error.includes('Extracted text is too short'),
  );
  if (maybeImageBasedPdfError) {
    return <ImageBasedPdfDisclaimer />;
  }

  const unsupported = findCodedError(errors, parseUnsupportedDocumentError);
  if (unsupported) {
    return (
      <UnsupportedDocumentAlert
        message={unsupported.message}
        rationale={unsupported.rationale}
      />
    );
  }

  const multiplePolicies = findCodedError(errors, parseMultiplePoliciesError);
  if (multiplePolicies) {
    return (
      <MultiplePoliciesAlert
        message={multiplePolicies.message}
        rationale={multiplePolicies.rationale}
      />
    );
  }

  return (
    <div className="my-2 rounded-md bg-yellow-50 p-3">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="h-5 w-5 text-yellow-400"
          />
        </div>
        <div className="ml-3">
          <h3 className="mb-1 text-sm font-medium text-yellow-800">Oops! Something went wrong.</h3>
          <p className="text-sm text-yellow-800">
            Please verify the policy document and try again.
          </p>
          <p className="text-sm text-yellow-800">
            If the issue persists, reach out to our support team for help.
          </p>
        </div>
      </div>
    </div>
  );
}

function UnsupportedDocumentAlert({ message, rationale }: { message: string; rationale?: string }) {
  return (
    <div className="my-2 rounded-md bg-yellow-50 p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert
          aria-hidden="true"
          className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500"
        />
        <div>
          <h3 className="text-sm font-medium text-yellow-800">
            We can&apos;t analyze this document yet
          </h3>
          <p className="mt-1 text-sm text-yellow-700">{message}</p>
          {rationale && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-medium text-yellow-800 hover:text-yellow-900">
                What did we find?
              </summary>
              <p className="mt-1.5 text-xs text-yellow-700">{rationale}</p>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function MultiplePoliciesAlert({ message, rationale }: { message: string; rationale?: string }) {
  return (
    <div className="my-2 rounded-md bg-yellow-50 p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert
          aria-hidden="true"
          className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500"
        />
        <div>
          <h3 className="text-sm font-medium text-yellow-800">
            This file looks like it has more than one policy
          </h3>
          <p className="mt-1 text-sm text-yellow-700">{message}</p>
          {rationale && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-medium text-yellow-800 hover:text-yellow-900">
                What did we find?
              </summary>
              <p className="mt-1.5 text-xs text-yellow-700">{rationale}</p>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

export function TableLoadingSkeleton() {
  const { status } = useSelector(selectPolicyExtractor);
  const policyExtractorIsLoading = status === 'initiated' || status === 'polling';
  return (
    <div
      className={`${policyExtractorIsLoading ? 'my-4 flex flex-col items-center justify-center gap-4 text-center' : 'hidden'}`}
    >
      <div
        role="status"
        className="w-full animate-pulse space-y-4 divide-y divide-gray-200 rounded border border-l-0 border-r-0 border-gray-200 p-4  md:p-6 "
      >
        {Array(5)
          .fill(0)
          .map((_, index) => (
            <div
              key={index}
              className={`flex items-center justify-between pt-${index === 0 ? 0 : 4}`}
            >
              <div>
                <div className="mb-2.5 h-2 w-24 rounded-full bg-gray-300 dark:bg-gray-500"></div>
                <div className="h-2 w-32 rounded-full bg-gray-200 dark:bg-gray-500"></div>
              </div>
              <div className="h-2 w-12 rounded-full bg-gray-300 dark:bg-gray-500"></div>
            </div>
          ))}
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );
}
